/**
 * Browser event bridge for SignalR `TimesheetRealtimeEvent` payloads.
 * @see TIMESHEET_SIGNALR_FRONTEND.md (server docs)
 */

/** @typedef {'timesheet.submitted'|'timesheet.approved'|'timesheet.rejected'|'timesheet.returned'} TimesheetRealtimeKind */

/**
 * @typedef {Object} TimesheetRealtimePayload
 * @property {number} [schemaVersion]
 * @property {TimesheetRealtimeKind} kind
 * @property {string} timeSheetId
 * @property {string} employeeUserId
 * @property {string} [employeeName]
 * @property {string} weekStartDate
 * @property {string} weekEndDate
 * @property {string} timeSheetStatus
 * @property {string|null|undefined} [approvalStatus]
 * @property {string|null|undefined} [approvalId]
 * @property {string|null|undefined} [remarks]
 */

export const TIMESHEET_REALTIME_EVENT = "hrm:timesheetRealtime";

export function normalizeTimesheetUserId(id) {
  if (id == null || id === "") return "";
  return String(id).trim();
}

/**
 * @param {string|undefined|null} weekStartYmd yyyy-MM-dd
 * @param {Date} monthStart first day of visible month
 */
export function timesheetWeekStartsInVisibleMonth(weekStartYmd, monthStart) {
  if (!weekStartYmd || !monthStart) return false;
  const parts = String(weekStartYmd).split("-");
  if (parts.length < 2) return false;
  const y = Number(parts[0]);
  const m = Number(parts[1]);
  if (!Number.isFinite(y) || !Number.isFinite(m)) return false;
  return y === monthStart.getFullYear() && m - 1 === monthStart.getMonth();
}

/**
 * @param {(payload: TimesheetRealtimePayload) => void} listener
 * @returns {() => void}
 */
export function subscribeTimesheetRealtime(listener) {
  if (typeof window === "undefined") return () => {};
  /** @param {CustomEvent} e */
  const handler = (e) => {
    listener(e.detail);
  };
  window.addEventListener(TIMESHEET_REALTIME_EVENT, handler);
  return () => window.removeEventListener(TIMESHEET_REALTIME_EVENT, handler);
}

/**
 * @param {TimesheetRealtimePayload} detail
 */
export function emitTimesheetRealtimeFromHub(detail) {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(TIMESHEET_REALTIME_EVENT, { detail }));
}
